import { useEffect, useMemo, useState } from 'react'
import { Calendar, Views, momentLocalizer } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { AgendasTypes, IAgenda, IAppointment, IPatient, SnackbarType, DEFAULT_CLINIC_TIMEZONE } from '../../../../constants/types';
import { patientFullName, fullDateFromPostgresString, timeFromPostgresString } from '../../../../utils';
import { safeLocaleCompare } from '../../../../utils/sort';
import Modal from '../../../../components/general/modal';
import { Alert, Button, Grid, Snackbar, Typography } from '@mui/material';

import { IconGenerator } from '../../../../components/general/mini_components';
import moment from 'moment';
import { turnsAgendaDates, isTimeSlotAvailableInAnyAgenda, canRescheduleAppointment } from '../../../../utils/agenda';
import { Translate } from 'react-localize-redux';
import { CustomEvent, eventStyleGetter } from './calendarStyles';
import { IRequest, RequestStatus, RequestType } from '../../Request/types';
import { HOSPITAL_PATIENT, HOSPITAL_OUTPATIENTS_CREATE_APPOINTMENT, HOSPITAL_BILLING_VIEW_DOCUMENT } from '../../../../routes/urls';
import NewAppointment from './NewAppointment';
import { FormMakeAppointment } from '../FormAppointment';
import { fetchPatient } from '../../../../db';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { 
    updateAppointmentService, 
    prepareAppointmentUpdateData, 
    parseAppointmentUpdateError 
} from '../../../../services/agenda';
import { PERMISSION } from '../../../../components/investigation/share/user_roles';
import QuickPayButton from '../../../../components/QuickPayButton';
import { ReduxStore } from '../../../../constants/types';

// Create the DragAndDropCalendar component
const DragAndDropCalendar = withDragAndDrop(Calendar);


interface Event {
    id: number;
    title: string;
    start: Date;
    end: Date;
    resourceId: number | number[]; // Adjust based on your resourceId type
}

export interface MultiAgendaProps {
    patients: IPatient[] // Replace 'any' with the actual type if known
    appointments: IAppointment[] // Replace 'any' with the actual type if known
    agendas: IAgenda[] // Replace 'any' with the actual type if known
    date: Date,
    typeAgenda:AgendasTypes,
    lastUpdate: number,
    showSnackbar:SnackbarType,
    extraForm? : number,
    canCreateAppointments: boolean,
    uuidInvestigation: string,
    request?: IRequest,
    preSelectedPatientUuid?: string,
    cancelCallback: (uuidAgenda:string, byUser:boolean) => void,
    showUpCallback: (uuidAgenda:string) => void,
    callbackSetSnackbar: (showSnackbar:SnackbarType) => void;
    appointmentCreatedCallback: (appointment:IAppointment) => void;
    appointmentUpdatedCallback?: (appointment:IAppointment) => void;
    appointmentErrorCallback: (error:any) => void;
    timezone?: string;
}

export default function MultiAgenda({ date, appointments, agendas, request, patients, showSnackbar, uuidInvestigation, lastUpdate, typeAgenda, extraForm, canCreateAppointments,
                                        preSelectedPatientUuid, callbackSetSnackbar, appointmentCreatedCallback, appointmentUpdatedCallback, appointmentErrorCallback, cancelCallback, showUpCallback, timezone }: MultiAgendaProps) {
    const clinicTimezone = timezone || DEFAULT_CLINIC_TIMEZONE;
    const [showModal, setShowModal] = useState(false);
    const [appointment, setAppointment] = useState<IAppointment | null>(null);
    const [newAppointment, setNewAppointment] = useState<IAppointment | null>(null);
    const [showNewAppointment, setShowNewAppointment] = useState(false);
    const [fetchedPatient, setFetchedPatient] = useState<IPatient | null>(null);
    const [loadingTransfer, setLoadingTransfer] = useState(false);
    
    // Transfer confirmation modal state
    const [showTransferConfirmation, setShowTransferConfirmation] = useState(false);
    const [pendingTransfer, setPendingTransfer] = useState<{
        appointment: IAppointment;
        newStart: Date;
        newEnd: Date;
        newAgendaId: number;
        newAgenda: IAgenda;
        updateData: any;
    } | null>(null);
    
    // Ghost copy state for drag preview
    const [ghostAppointment, setGhostAppointment] = useState<{
        id: number;
        originalStart: Date;
        originalEnd: Date;
        originalResourceId: number;
        title: string;
        uuidPatient: string;
        reason: string;
        notes: string;
        type: RequestStatus;
        surgeryIndication?: string;
        surgeon?: any;
        surgicalTeam?: any[];
        serviceDescription?: string;
    } | null>(null);
    
    const history = useHistory();
    const researchers = useSelector((state: any) => 
        state.hospital.data.researchers ? state.hospital.data.researchers : []
      );
    const permissions = useSelector((state: any) =>
        state.investigations.currentInvestigation?.permissions ?? []
    );
    const currency = useSelector((state: any) => state.investigations.currentInvestigation?.billingInfo?.currency ?? 'FCFA');
    const activeLanguageCode = useSelector((state: any) => state.localize?.languages?.[state.localize?.activeLanguage]?.code ?? 'fr');
    const translateString = useMemo(() => {
        return (`pages.hospital.outpatients.calendar.modal.${typeAgenda === AgendasTypes.CONSULTATION ? "outpatients" : "surgery"}`);
    }, [typeAgenda])

    const events = useMemo(() => {
        const allEvents = appointments.map((appointment, index) => {
            const uuidPatient = appointment.patient.uuid;
            const patient = patients.find(pat => pat.uuid === uuidPatient);
            const patientName = patient ? patientFullName(patient?.personalData) : "Unknown";
            const startDateTime = new Date(appointment.startDateTime);
            const twoHoursAgo = new Date(new Date().getTime() - 2 * 60 * 60 * 1000);
            const appointmentStatus = (appointment.requestAppointment.status === RequestStatus.PENDING_APPROVAL) && startDateTime < twoHoursAgo ? RequestStatus.EXPIRED : appointment.requestAppointment.status;
            const surgeryIndication = appointment.requestAppointment.typeSpecificData?.ict;
            console.log("Surgeon: ", appointment?.mainSurgeon);
            const surgeon = appointment.mainSurgeon ? researchers.find(researcher => researcher.uuid === appointment.mainSurgeon.researcher.uuid) : null;
            const surgicalTeam = appointment.surgicalTeam ? appointment.surgicalTeam.map(team => researchers.find(researcher => researcher.uuid === team.researcher.uuid)) : [];
            console.log(appointment.requestAppointment.service);
            const serviceDescription = appointment.requestAppointment ? appointment.requestAppointment.service.description : null;
            console.log("Surgery Indication: ", surgeryIndication);
            console.log("StartDate: ", startDateTime);
            return {
                id: appointment.id,
                title: patientName,
                uuidPatient: uuidPatient,
                billUuid: appointment.billUuid,
                requestServiceInvestigationId: appointment.requestServiceInvestigationId,
                reason: appointment.reasonVisit,
                notes: appointment.notes,
                type: appointmentStatus,
                start: startDateTime,
                surgeryIndication: surgeryIndication,
                surgeon: surgeon,
                surgicalTeam: surgicalTeam,
                serviceDescription: serviceDescription,
                end: new Date(appointment.endDateTime),
                resourceId: appointment.agendaId,
                onQuickPaySuccess:
                    appointment.billUuid && appointmentUpdatedCallback
                        ? () =>
                              appointmentUpdatedCallback({
                                  ...appointment,
                                  requestAppointment: {
                                      ...appointment.requestAppointment,
                                      status: RequestStatus.ACCEPTED,
                                  },
                              })
                        : undefined,
            }
        });

        return allEvents;
    }, [appointments, lastUpdate, ghostAppointment, appointmentUpdatedCallback, patients, researchers])
    
    const resourceMap = useMemo(() => {
        return agendas.sort((a, b) => safeLocaleCompare(a.name, b.name)).map((agenda, index) => {
            return {
                resourceId: agenda.id,
                resourceTitle: agenda.name
            }
        })
    }, [agendas, lastUpdate])
    
    const [minHour, maxHour] = useMemo(() => {
        let minHour = 24;
        let maxHour = 0;
        agendas.forEach(agenda => {
            const [startTurn, endTurn] = turnsAgendaDates(agenda.turn);
            const startHour = startTurn.getHours();
            const endHour = endTurn.getHours();
            if(startHour < minHour){
                minHour = startHour;
            }
            if(endHour > maxHour){
                maxHour = endHour;
            }
        })
        return [minHour, maxHour];
    }, [agendas])
    
    const { defaultDate, views } = useMemo(
        () => ({
            defaultDate: date,
            views: ['day'],
        }),
        [date]
    )

    useEffect(() => {
        resetModal();
    }, [appointments.length])

    useEffect(() => {
        if (showSnackbar.show && showSnackbar.severity === "success") {
            setShowModal(false);
            setAppointment(null);
            setNewAppointment(null);
            setFetchedPatient(null);
            setShowNewAppointment(false);
        }
    }, [showSnackbar.show]);

    useEffect(() => {
        async function fetchPatientData() {
            if (showNewAppointment && appointment) {
                const patient = await fetchPatient(appointment.patient.uuid);
                setFetchedPatient(patient);
            }
        }
        fetchPatientData();
    }, [showNewAppointment, appointment]);

    function handleSelectEvent(event:Event) {
        console.log("Selected event: ", event);
        setShowModal(true);
        const appointment = appointments.find(app => app.id === event.id);
        setAppointment(appointment);
    }

    function cancelAppointment(byUser:boolean){
        if(appointment){
            cancelCallback(appointment.uuid, byUser);
        }
    }

    function showUpAppointment(){
        if(appointment){
            showUpCallback(appointment.uuid);
        }
    }

    function sheduleNewAppointment(){
        if(typeAgenda === AgendasTypes.CONSULTATION && appointment){
            // Navigate to the create appointment page with pre-filled data
            const agenda = agendas.find(a => a.id === appointment.agendaId);
            const startDate = new Date(appointment.startDateTime);
            const queryParams = new URLSearchParams({
                uuidPatient: appointment.patient.uuid,
                agendaId: appointment.agendaId.toString(),
                date: startDate.toISOString()
            });
            
            // Add department UUID if agenda has a department
            if(agenda?.department?.uuid){
                queryParams.set('departmentId', agenda.department.uuid);
            }
            
            history.push(`${HOSPITAL_OUTPATIENTS_CREATE_APPOINTMENT}?${queryParams.toString()}`);
        } else {
            // For surgery or other cases, keep the old behavior
            setShowNewAppointment(true);
        }
    }

    function renderAllOptionsModalCore(typeAgenda:AgendasTypes){
        
        return(<>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Button onClick={() => cancelAppointment(false)}>
                        <IconGenerator type="cancel_appointment" size="large" />
                    </Button>
                    <Typography variant="body1" component="span" gutterBottom>
                        <Translate id={`${translateString}.cancel_appoinment`} />
                    </Typography>
                </Grid>
                {
                    appointment?.requestAppointment.status !== RequestStatus.CHECKED_IN &&
                    <Grid item xs={12}>
                        <Button onClick={showUpAppointment}>
                            <IconGenerator type="show_up" size="large" />
                        </Button>  
                        <Typography variant="body1" component="span" gutterBottom>
                            <Translate id={`${translateString}.check_in_patient`} />
                        </Typography>  
                    </Grid>
                }
                {
                    typeAgenda === AgendasTypes.CONSULTATION &&
                <>
                <Grid item xs={12}>
                    <Button onClick={sheduleNewAppointment}>
                        <IconGenerator color="black" type="outpatients" size="large" />
                    </Button>     
                    <Typography variant="body1" component="span" gutterBottom>
                        <Translate id={`${translateString}.another_appointment`} />
                    </Typography>             
                </Grid>
                
                <Grid item xs={12}>
                    <Button onClick={() => cancelAppointment(true)}>
                        <IconGenerator type="cancel_appointment_by_user" size="large" />
                    </Button>
                    <Typography variant="body1" component="span" gutterBottom>
                        <Translate id={`${translateString}.cancel_appoinment_by_user`} />
                        </Typography>
                        </Grid>
                    </>
                }
            </Grid>
        </>)
    }

    function renderPendingPaymentModalCore(){
        return(<>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Button onClick={() => cancelAppointment(false)}>
                    <IconGenerator type="cancel_appointment" size="large" />
                </Button>
                <Typography variant="body1" component="span" gutterBottom>
                    <Translate id={`${translateString}.cancel_appoinment`} />
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Button onClick={sheduleNewAppointment}>
                    <IconGenerator color="black" type="outpatients" size="large" />
                </Button>     
                <Typography variant="body1" component="span" gutterBottom>
                    <Translate id={`${translateString}.title`} />
                </Typography>             
            </Grid>
        </Grid>
        </>)
    }
    function renderModalAppointmentCore(){
        if(typeAgenda === AgendasTypes.CONSULTATION){
            switch(appointment?.requestAppointment.status){
                case RequestStatus.PENDING_APPROVAL:
                    return (
                        renderAllOptionsModalCore(typeAgenda)
                    );
                case RequestStatus.CANCELED_BY_USER:
                case RequestStatus.PENDING_APPROVAL:
                    return (
                        renderPendingPaymentModalCore()
                    );
                case RequestStatus.PENDING_PAYMENT:
                    return (
                        <Grid container spacing={2}>
                            <Grid item xs={12} style={{paddingTop:'1rem'}}>
                                <Typography variant="body1" component="p" >
                                    <Translate id="pages.hospital.outpatients.table_patient_appointments.modal.pending_payment.message" />
                                </Typography>
                            </Grid>
                            
                            
                            {/* View Clinical History Button - Only show if user can view medical records */}
                            {permissions.includes(PERMISSION.MEDICAL_READ) && (
                                <Grid item xs={12}>
                                    <Button onClick={() => {
                                        const nextUrl = HOSPITAL_PATIENT.replace(":uuidPatient", appointment!.patient.uuid)
                                        window.open(nextUrl, '_blank');
                                    }}>
                                        <IconGenerator type="view" size="medium" />
                                    </Button>
                                    <Typography variant="body1" component="span" gutterBottom>
                                        <Translate id="pages.hospital.outpatients.calendar.modal.outpatients.view_clinical_history" />
                                    </Typography>
                                </Grid>
                            )}

                            {appointment?.billUuid && (
                                <Grid item xs={12}>
                                    <QuickPayButton
                                        uuidInvestigation={uuidInvestigation}
                                        billUuid={appointment.billUuid}
                                        currency={currency}
                                        languageCode={activeLanguageCode}
                                        showInlineSuccess={false}
                                        onSuccess={() => {
                                            if (appointment && appointmentUpdatedCallback) {
                                                appointmentUpdatedCallback({
                                                    ...appointment,
                                                    requestAppointment: {
                                                        ...appointment.requestAppointment,
                                                        status: RequestStatus.ACCEPTED,
                                                    },
                                                });
                                            }
                                            resetModal();
                                            callbackSetSnackbar({
                                                show: true,
                                                severity: 'success',
                                                message: 'pages.hospital.outpatients.quick_pay.success',
                                            });
                                        }}
                                    />
                                    <Typography variant="body1" component="span" gutterBottom style={{ marginLeft: '0.5rem' }}>
                                        <Translate id="pages.hospital.outpatients.quick_pay.tooltip" />
                                    </Typography>
                                </Grid>
                            )}

                            {/* View Bill Button - Only show if billUuid exists */}
                            {appointment?.billUuid && (
                                <Grid item xs={12}>
                                    <Button onClick={() => {
                                        const billUrl = HOSPITAL_BILLING_VIEW_DOCUMENT.replace(":uuidDocument", appointment!.billUuid!)
                                        window.open(billUrl, '_blank');
                                    }}>
                                        <IconGenerator type="billing" size="large" />
                                    </Button>
                                    <Typography variant="body1" component="span" gutterBottom>
                                        <Translate id="pages.hospital.outpatients.calendar.modal.outpatients.view_bill" />
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    ); 
                default:
                    const nextUrl = HOSPITAL_PATIENT.replace(":uuidPatient", appointment!.patient.uuid)
                    console.log("Next url", nextUrl);
                    //history.push(nextUrl);
                    window.open(nextUrl, '_blank');
                    resetModal();
            }
        }
        else if(typeAgenda === AgendasTypes.SURGERY){
            return (
                renderAllOptionsModalCore(typeAgenda)
            );
        }
    }

    function renderCoreModal() {
        if (showNewAppointment && appointment && fetchedPatient) {
            return (
                <FormMakeAppointment
                    uuidPatient={appointment.patient.uuid}
                    uuidInvestigation={uuidInvestigation}
                    showAllAgendas={false}
                    hidePatientInfo={false}
                    extraForm={extraForm}
                    typeAgenda={typeAgenda}
                    dateTimeAppointment={true}
                    cancelCallback={resetModal}
                    phoneNumber={fetchedPatient.personalData.phone}
                    request={request}
                    appointmentMadeCallback={(date) => appointmentCreatedCallback(date)}
                    
                />
            );
        } else if (appointment) {
            return renderModalAppointmentCore();
        } else if (newAppointment) {
            return (
                <NewAppointment
                    request={request}
                    appointmentInfo={newAppointment}
                    extraForm={extraForm}
                    typeAgenda={typeAgenda}
                    preSelectedPatientUuid={preSelectedPatientUuid}
                    cancelCallback={resetModal}
                    appointmentCreatedCallback={appointmentCreatedCallback}
                    appointmentErrorCallback={appointmentErrorCallback}
                />
            );
        }
    }

    function resetModal() {
        setShowModal(false);
        setAppointment(null);
        setNewAppointment(null);
        setFetchedPatient(null);
        setShowNewAppointment(false);
    }

    function slotPropGetter(date: Date, resourceId?: string | number) {
        const numericResourceId = typeof resourceId === 'string' ? parseInt(resourceId, 10) : resourceId;
        const isAvailable = isTimeSlotAvailableInAnyAgenda(date, agendas, numericResourceId);
        
        if (!isAvailable) {
            return {
                style: {
                    backgroundColor: '#d0d0d0',
                    opacity: 1,
                    cursor: 'not-allowed',
                },
            };
        }
        
        return {};
    }

    function handleOnClickSlot(slotInfo:any){
        if(!canCreateAppointments){
            return null;
        }
        const { start, resourceId } = slotInfo;
        console.log('Start time:', start);
        console.log('Resource ID:', resourceId);

        // Check if the time slot is available
        const isAvailable = isTimeSlotAvailableInAnyAgenda(start, agendas, resourceId);
        if (!isAvailable) {
            callbackSetSnackbar({
                show: true,
                severity: 'warning',
                message: 'pages.hospital.outpatients.appointment.error.outside_agenda_hours'
            });
            return;
        }

        const currentAgenda = agendas.find(agenda => agenda.id === resourceId);
        console.log('Current Agenda:', currentAgenda);
        // Get just the hour of the clicked date
        const clickedHour = start.getHours();
        console.log('Clicked Hour:', clickedHour);
        const newAppointment = {
            startDateTime: start,
            agendaId: resourceId,
        }
        setNewAppointment(newAppointment);
        setShowModal(true);
        
    }

    // Drag-and-Drop Handlers
    function handleDragStart({ event }: { event: any }) {
        // Find the appointment being dragged
        const draggedAppointment = appointments.find(app => app.id === event.id);
        if (!draggedAppointment) return;

        // Create ghost copy
        setGhostAppointment({
            id: event.id,
            originalStart: event.start,
            originalEnd: event.end,
            originalResourceId: event.resourceId,
            title: event.title,
            uuidPatient: event.uuidPatient,
            reason: event.reason,
            notes: event.notes,
            type: event.type,
            surgeryIndication: event.surgeryIndication,
            surgeon: event.surgeon,
            surgicalTeam: event.surgicalTeam,
            serviceDescription: event.serviceDescription
        });
    }

    function handleEventDrop({ event, start, end, resourceId }: {
        event: any;
        start: Date;
        end: Date;
        resourceId?: number;
    }) {
        // Find the appointment being dragged
        const draggedAppointment = appointments.find(app => app.id === event.id);
        if (!draggedAppointment) {
            console.error('Appointment not found:', event.id);
            return;
        }

        // Check if appointment can be rescheduled (matches backend: not completed, not cancelled)
        if (!canRescheduleAppointment(draggedAppointment.requestAppointment.status)) {
            callbackSetSnackbar({
                show: true,
                severity: 'warning',
                message: 'pages.hospital.outpatients.appointment.error.cannot_transfer'
            });
            return;
        }

        // Get the new agenda
        const newAgenda = agendas.find(a => a.id === resourceId);
        if (!newAgenda) {
            console.error('Target agenda not found:', resourceId);
            return;
        }

        // Check if anything actually changed
        const agendaChanged = draggedAppointment.agendaId !== resourceId;
        const timeChanged = new Date(draggedAppointment.startDateTime).getTime() !== start.getTime();
        
        if (!agendaChanged && !timeChanged) {
            // No changes, nothing to do
            return;
        }

        // Validate time slot availability
        const isAvailable = isTimeSlotAvailableInAnyAgenda(start, agendas, resourceId);
        if (!isAvailable) {
            callbackSetSnackbar({
                show: true,
                severity: 'error',
                message: 'pages.hospital.outpatients.appointment.error.slot_not_available'
            });
            return;
        }

        // Prepare update data
        const updateData = prepareAppointmentUpdateData(
            draggedAppointment,
            agendaChanged ? newAgenda.uuid : undefined,
            timeChanged ? start : undefined
        );

        // Show confirmation modal instead of immediately transferring
        setPendingTransfer({
            appointment: draggedAppointment,
            newStart: start,
            newEnd: end,
            newAgendaId: resourceId,
            newAgenda: newAgenda,
            updateData: updateData
        });
        setShowTransferConfirmation(true);
    }

    // Confirm and execute the transfer
    async function confirmTransfer() {
        if (!pendingTransfer) return;

        const { appointment: draggedAppointment, updateData } = pendingTransfer;

        try {
            setLoadingTransfer(true);
            setShowTransferConfirmation(false);
            
            const response = await updateAppointmentService(
                uuidInvestigation,
                draggedAppointment.uuid,
                typeAgenda,
                updateData
            );

            // Show success message
            callbackSetSnackbar({
                show: true,
                severity: 'success',
                message: 'pages.hospital.outpatients.appointment.transfer.success'
            });
            
            // Remove ghost appointment on success
            setGhostAppointment(null);
            
            // Notify parent to update the appointment (not create a new one)
            if (appointmentUpdatedCallback) {
                appointmentUpdatedCallback(response.appointment);
            } else {
                // Fallback to created callback if updated callback not provided
                appointmentCreatedCallback(response.appointment);
            }
            
        } catch (error) {
            console.error('Appointment transfer error:', error);
            
            // Use error parser helper
            const messageKey = parseAppointmentUpdateError(error);
            
            callbackSetSnackbar({
                show: true,
                severity: 'error',
                message: messageKey
            });

            // On error, revert the appointment to original position
            // by removing ghost and triggering a refresh
            setGhostAppointment(null);
            // Force calendar to revert by calling the callback with a fake update
            appointmentCreatedCallback(draggedAppointment);
        } finally {
            setLoadingTransfer(false);
            setPendingTransfer(null);
        }
    }

    // Cancel the transfer
    function cancelTransfer() {
        if (!pendingTransfer) return;

        setShowTransferConfirmation(false);
        
        // Remove ghost appointment
        setGhostAppointment(null);
        
        // Revert appointment to original position by triggering a refresh
        appointmentCreatedCallback(pendingTransfer.appointment);
        
        setPendingTransfer(null);
    }

    function handleEventResize({ event, start, end }: {
        event: any;
        start: Date;
        end: Date;
    }) {
        // For now, we'll disable resize functionality
        // Could be implemented later if needed
        console.log('Event resize not implemented yet');
    }

    if(agendas.length === 0){
        return "No Agendas"
    }
    return (
    <>
        <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={showSnackbar.show}
                autoHideDuration={2000}
                onClose={() => callbackSetSnackbar({ show: false })}>
                <div>
                    {
                        (showSnackbar.message && showSnackbar.severity) &&
                        <Alert onClose={() => callbackSetSnackbar({ show: false })} severity={showSnackbar.severity}>
                            <Translate id={showSnackbar.message} />
                        </Alert>
                    }
                </div>
            </Snackbar>
        <Modal open={showModal} closeModal={resetModal} 
            title={appointment ? <Translate id={`${translateString}.existing_appointment.title`} /> : <Translate id={`${translateString}.new_appointment.title`   } />} >
            <>
            {
                renderCoreModal()
            }
            </>
        </Modal>
        
        {/* Transfer Confirmation Modal */}
        <Modal 
            open={showTransferConfirmation} 
            closeModal={cancelTransfer}
            title={<Translate id="pages.hospital.outpatients.appointment.transfer.confirm_title" />}
        >
            <>
                {pendingTransfer && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                <Translate id="pages.hospital.outpatients.appointment.transfer.confirm_message" />
                            </Typography>
                        </Grid>

                        {/* Patient Info */}
                        <Grid item xs={12}>
                            <Typography variant="body1">
                                <strong><Translate id="pages.hospital.outpatients.appointment.transfer.patient" />:</strong>{' '}
                                {(() => {
                                    const patient = patients.find(p => p.uuid === pendingTransfer.appointment.patient.uuid);
                                    return patient ? patientFullName(patient.personalData) : 'Unknown';
                                })()}
                            </Typography>
                        </Grid>

                        {/* Previous Agenda */}
                        {pendingTransfer.appointment.agendaId !== pendingTransfer.newAgendaId && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        <Translate id="pages.hospital.outpatients.appointment.transfer.from_agenda" />:
                                    </Typography>
                                    <Typography variant="body1">
                                        {agendas.find(a => a.id === pendingTransfer.appointment.agendaId)?.name || 'Unknown'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        <Translate id="pages.hospital.outpatients.appointment.transfer.to_agenda" />:
                                    </Typography>
                                    <Typography variant="body1" color="primary">
                                        <strong>{pendingTransfer.newAgenda.name}</strong>
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        {/* Previous DateTime */}
                        {new Date(pendingTransfer.appointment.startDateTime).getTime() !== pendingTransfer.newStart.getTime() && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        <Translate id="pages.hospital.outpatients.appointment.transfer.from_datetime" />:
                                    </Typography>
                                    <Typography variant="body1">
                                        {fullDateFromPostgresString('en-GB', pendingTransfer.appointment.startDateTime, clinicTimezone)} {timeFromPostgresString('en-GB', pendingTransfer.appointment.startDateTime, clinicTimezone)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        <Translate id="pages.hospital.outpatients.appointment.transfer.to_datetime" />:
                                    </Typography>
                                    <Typography variant="body1" color="primary">
                                        <strong>{moment(pendingTransfer.newStart).format('DD/MM/YYYY HH:mm')}</strong>
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        {/* Action Buttons */}
                        <Grid item xs={12} style={{ marginTop: '1rem' }}>
                            <Grid container spacing={2} justifyContent="flex-end">
                                <Grid item>
                                    <Button 
                                        variant="outlined" 
                                        onClick={cancelTransfer}
                                        disabled={loadingTransfer}
                                    >
                                        <Translate id="pages.hospital.outpatients.appointment.transfer.cancel" />
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        onClick={confirmTransfer}
                                        disabled={loadingTransfer}
                                    >
                                        {loadingTransfer ? (
                                            <Translate id="pages.hospital.outpatients.appointment.transfer.confirming" />
                                        ) : (
                                            <Translate id="pages.hospital.outpatients.appointment.transfer.confirm" />
                                        )}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </>
        </Modal>
        
        <div className="height600">
            <DragAndDropCalendar
                key={defaultDate.toISOString()}
                defaultDate={defaultDate}
                defaultView={Views.DAY}
                events={events}
                localizer={momentLocalizer(moment)}
                resourceIdAccessor="resourceId"
                resources={resourceMap}
                formats={{ eventTimeRangeFormat: () => null }}
                resourceTitleAccessor="resourceTitle"
                step={15}
                timeslots={1} 
                selectable={true} 
                onSelectSlot = {handleOnClickSlot}
                views={views}
                onSelectEvent={handleSelectEvent}
                onSelecting={() => false}
                onDragStart={handleDragStart}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventResize}
                resizable={false}
                draggableAccessor={(event) => {
                    // Draggable when reschedule is allowed (matches backend: not completed, not cancelled)
                    if ((event as any).isGhost) return false;
                    const appt = appointments.find(app => app.id === event.id);
                    return appt ? canRescheduleAppointment(appt.requestAppointment.status) : false;
                }}
                components={{
                    toolbar: () => null,
                    event: CustomEvent
                }}
                eventPropGetter={(event: any, start: any, end: any, isSelected: boolean) => {
                    // Apply ghost styling
                    if (event.isGhost) {
                        const baseStyle = eventStyleGetter(event, start, end, isSelected);
                        return {
                            ...baseStyle,
                            style: {
                                ...baseStyle.style,
                                opacity: 0.5,
                                pointerEvents: 'none',
                                cursor: 'default'
                            }
                        };
                    }
                    return eventStyleGetter(event, start, end, isSelected);
                }}
                slotPropGetter={slotPropGetter}
                style={{ height: 1000, opacity: loadingTransfer ? 0.6 : 1 }}
                min={new Date(2024, 8, 21, minHour, 0, 0)} // 8:00 AM
                max={new Date(2024, 8, 21, maxHour, 0, 0)} // 6:00 PM
            />
      </div>
    </>
  )
}
